import { useEffect, useMemo, useState } from 'react';
import { api, ApiError } from '../../lib/api';
import { useSession } from '../../lib/store';
import { Card, Empty, Notice, PageHeading, Row, Spinner } from '../../components/Shell';
import { AssessmentStamp, Stamp, VaccinationStamp } from '../../components/Stamp';
import { SelectField, TextArea, TextField, Toggle } from '../../components/Field';
import { CopyLink } from '../../components/CopyLink';
import { longDate, titleCase } from '../../lib/format';
import type {
  AgreementSummary,
  AssessmentStatus,
  ClientInviteSummary,
  ClientProfile,
  Pet,
  PetDocument,
} from '../../lib/types';

/**
 * The client directory: who is on the books, and whose paperwork is behind.
 * Opening a client shows the full file, including the home access detail that
 * is deliberately hidden from the list.
 */
export function AdminClients() {
  const session = useSession();
  const tenantId = session.tenant?.tenantId;

  const [clients, setClients] = useState<ClientProfile[]>([]);
  const [agreements, setAgreements] = useState<AgreementSummary[]>([]);
  const [invites, setInvites] = useState<ClientInviteSummary[]>([]);
  const [search, setSearch] = useState('');
  const [openClientId, setOpenClientId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!tenantId) return;
    void Promise.all([
      api<{ clients: ClientProfile[] }>(`/tenants/${tenantId}/clients`),
      api<{ agreements: AgreementSummary[] }>(`/tenants/${tenantId}/agreements`),
      api<{ invites: ClientInviteSummary[] }>(`/tenants/${tenantId}/clients/invites`),
    ])
      .then(([clientResult, agreementResult, inviteResult]) => {
        setClients(clientResult.clients);
        setAgreements(agreementResult.agreements);
        setInvites(inviteResult.invites);
      })
      .catch((err) => setError(err instanceof ApiError ? err.message : 'Clients could not be loaded.'))
      .finally(() => setLoading(false));
  }, [tenantId]);

  /** Fold an invitation the API has just written into what is on screen. */
  const absorbInvite = (invite: ClientInviteSummary, client: ClientProfile) => {
    setInvites((prev) => [invite, ...prev.filter((i) => i.email !== invite.email)]);
    setClients((prev) =>
      prev.some((c) => c.clientId === client.clientId)
        ? prev.map((c) => (c.clientId === client.clientId ? client : c))
        : [...prev, client]
    );
  };

  const outstanding = useMemo(
    () => invites.filter((invite) => invite.status !== 'accepted'),
    [invites]
  );

  const signedByClient = useMemo(() => {
    const map = new Set<string>();
    agreements.filter((a) => a.status === 'signed').forEach((a) => map.add(a.clientId));
    return map;
  }, [agreements]);

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return clients;
    return clients.filter(
      (c) =>
        c.fullName.toLowerCase().includes(query) ||
        c.email.toLowerCase().includes(query) ||
        (c.mobile ?? '').includes(query)
    );
  }, [clients, search]);

  if (loading) {
    return (
      <div className="mx-auto max-w-4xl px-5 py-16">
        <Spinner label="Loading clients" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl px-5 pb-24 pt-12">
      <PageHeading eyebrow={session.tenant?.businessName} title="Clients" />

      {error && (
        <div className="mb-8">
          <Notice tone="error">{error}</Notice>
        </div>
      )}

      {tenantId && <InvitePanel tenantId={tenantId} onInvited={absorbInvite} />}

      {outstanding.length > 0 && <OutstandingInvites invites={outstanding} />}

      <div className="mb-8 max-w-sm">
        <TextField label="Find someone" value={search} onChange={setSearch} placeholder="Name, email or number" />
      </div>

      {filtered.length === 0 ? (
        <Empty
          title={search ? 'Nobody matches that' : 'No clients yet'}
          body={
            search
              ? 'Try a different name or number.'
              : 'Invite someone by email above, or send them your booking page and let them start on their own. Either way they land here.'
          }
        />
      ) : (
        <ul className="space-y-3">
          {filtered.map((client) => (
            <li key={client.clientId}>
              <Card className="p-4">
                <button
                  type="button"
                  className="flex w-full flex-wrap items-center justify-between gap-4 text-left"
                  onClick={() =>
                    setOpenClientId((current) => (current === client.clientId ? null : client.clientId))
                  }
                  aria-expanded={openClientId === client.clientId}
                >
                  <div>
                    <p className="font-display text-lg font-bold">{client.fullName}</p>
                    <p className="typed text-sm text-[color:var(--color-ink-soft)]">
                      {client.email}
                      {client.mobile && ` · ${client.mobile}`}
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    {/* A lead has been asked and has not started, so "not
                        signed" would be technically true and useless: there is
                        no agreement to chase until they have an account. */}
                    {client.status === 'lead' ? (
                      <Stamp tone="quiet" flat>
                        Invited
                      </Stamp>
                    ) : signedByClient.has(client.clientId) ? (
                      <Stamp tone="ok" flat>
                        Agreement signed
                      </Stamp>
                    ) : (
                      <Stamp tone="flag" flat>
                        Not signed
                      </Stamp>
                    )}
                    {client.messaging?.optedOutAt && (
                      <Stamp tone="quiet" flat title="They replied STOP, or switched texts off in their portal">
                        No texts
                      </Stamp>
                    )}
                    {client.hasHomeAccessOnFile && (
                      <Stamp tone="quiet" flat>
                        Home access on file
                      </Stamp>
                    )}
                  </div>
                </button>

                {openClientId === client.clientId && tenantId && (
                  <ClientFile tenantId={tenantId} clientId={client.clientId} />
                )}
              </Card>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * Invite a prospective client by email.
 *
 * The one field that matters is the address, because that is all a sitter has
 * after meeting someone at the park. A name and a line of their own are
 * optional and both go into the email, since an invitation that reads like a
 * person wrote it gets opened and one that reads like a system does not.
 *
 * The link comes back so it can be copied. Until text messaging exists, a
 * sitter who would rather send it themselves is one paste away from doing so,
 * and the link works identically either way.
 */
function InvitePanel({
  tenantId,
  onInvited,
}: {
  tenantId: string;
  onInvited: (invite: ClientInviteSummary, client: ClientProfile) => void;
}) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [mobile, setMobile] = useState('');
  const [invitedName, setInvitedName] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sentTo, setSentTo] = useState<{
    email: string;
    joinUrl: string;
    delivered: boolean;
    texted: boolean;
  } | null>(null);

  const send = (event: React.FormEvent) => {
    event.preventDefault();
    setSending(true);
    setError(null);
    setSentTo(null);

    void api<{
      invite: ClientInviteSummary;
      client: ClientProfile;
      joinUrl: string;
      sent: boolean;
      texted: boolean;
    }>(`/tenants/${tenantId}/clients/invite`, {
      method: 'POST',
      body: {
        email,
        mobile: mobile || undefined,
        invitedName: invitedName || undefined,
        message: message || undefined,
      },
    })
      .then((result) => {
        onInvited(result.invite, result.client);
        setSentTo({ email, joinUrl: result.joinUrl, delivered: result.sent, texted: result.texted });
        setEmail('');
        setMobile('');
        setInvitedName('');
        setMessage('');
      })
      .catch((err) => setError(err instanceof ApiError ? err.message : 'That invitation could not be sent.'))
      .finally(() => setSending(false));
  };

  if (!open) {
    return (
      <div className="mb-8">
        <button type="button" className="btn btn-primary" onClick={() => setOpen(true)}>
          Invite a client
        </button>
        {sentTo && (
          <p className="mt-3 text-sm text-[color:var(--color-ink-soft)]">
            Invitation sent to <span className="typed">{sentTo.email}</span>.
          </p>
        )}
      </div>
    );
  }

  return (
    <Card className="mb-8 p-6">
      <form onSubmit={send} className="space-y-5">
        <div>
          <h2 className="font-display text-xl font-bold">Invite a client</h2>
          <p className="mt-1 text-sm text-[color:var(--color-ink-soft)]">
            They get a link to your new client pack: their details, a page per pet, and your agreement to
            sign. Their file opens here as soon as you send it.
          </p>
        </div>

        {error && <Notice tone="error">{error}</Notice>}

        <TextField
          label="Their email"
          type="email"
          value={email}
          onChange={setEmail}
          placeholder="them@example.com"
          autoComplete="off"
          required
        />
        <TextField
          label="Their mobile"
          type="tel"
          value={mobile}
          onChange={setMobile}
          hint="Optional. If you have it, the invitation is texted as well as emailed, which is what gets opened."
        />
        <TextField
          label="Their name"
          value={invitedName}
          onChange={setInvitedName}
          hint="Optional. Used to greet them, and they can correct it."
        />
        <TextArea
          label="A note from you"
          value={message}
          onChange={setMessage}
          rows={3}
          hint="Optional. Shown in the email and on the page they land on."
        />

        <div className="flex flex-wrap gap-3">
          <button type="submit" className="btn btn-primary" disabled={sending || !email}>
            {sending ? 'Sending' : 'Send invitation'}
          </button>
          <button type="button" className="btn btn-quiet" onClick={() => setOpen(false)} disabled={sending}>
            Done
          </button>
        </div>
      </form>

      {sentTo && (
        <div className="mt-6 border-t border-[color:var(--color-rule)] pt-6">
          <Notice tone={sentTo.delivered || sentTo.texted ? 'ok' : 'warn'}>
            {sentTo.delivered
              ? `Invitation sent to ${sentTo.email}${sentTo.texted ? ', and texted to them too' : ''}.`
              : sentTo.texted
                ? `${sentTo.email} was texted the invitation, but the email did not go out.`
                : `${sentTo.email} is invited, but nothing went out. Send them the link yourself.`}
          </Notice>
          <div className="mt-4">
            <CopyLink url={sentTo.joinUrl} label="Their invitation link, if you would rather text it" />
          </div>
        </div>
      )}
    </Card>
  );
}

/**
 * Invitations that have gone out and not been taken up. Accepted ones are left
 * off: that person is a client now, and the client list below is where they
 * belong. What is left is the chase list.
 */
function OutstandingInvites({ invites }: { invites: ClientInviteSummary[] }) {
  return (
    <section className="mb-8">
      <h2 className="eyebrow mb-3">Waiting on them</h2>
      <ul className="space-y-2">
        {invites.map((invite) => (
          <li
            key={invite.email}
            className="flex flex-wrap items-center justify-between gap-3 border border-[color:var(--color-rule)] px-4 py-3"
          >
            <div className="min-w-0">
              <p className="font-display font-bold">{invite.invitedName || invite.email}</p>
              <p className="typed text-sm text-[color:var(--color-ink-soft)]">
                {invite.invitedName && `${invite.email} · `}
                Invited {longDate(invite.createdAt)}
              </p>
            </div>
            {invite.status === 'expired' ? (
              <Stamp tone="flag" flat>
                Link expired
              </Stamp>
            ) : invite.viewedAt ? (
              // Opened but not finished is a different problem from never
              // opened, and it is the one worth a nudge.
              <Stamp tone="quiet" flat title={`Opened ${longDate(invite.viewedAt)}`}>
                Opened, not started
              </Stamp>
            ) : (
              <Stamp tone="quiet" flat>
                Not opened yet
              </Stamp>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

/** A staff-chosen assessment outcome, not yet saved. */
type AssessmentDraft = {
  status: Extract<AssessmentStatus, 'scheduled' | 'passed' | 'failed'>;
  scheduledFor: string;
  sleepoverRequired: boolean;
  notes: string;
};

const RECORDABLE_STATUSES: { value: AssessmentDraft['status']; label: string }[] = [
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'passed', label: 'Passed' },
  { value: 'failed', label: 'Failed' },
];

/** `<input type="datetime-local">` wants local time with no timezone suffix. */
function toDatetimeLocal(iso?: string): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function draftFromAssessment(pet: Pet): AssessmentDraft {
  const { assessment } = pet;
  const status = assessment.status === 'not_started' ? 'scheduled' : assessment.status;
  return {
    status,
    scheduledFor: toDatetimeLocal(assessment.scheduledFor),
    sleepoverRequired: assessment.sleepoverRequired ?? false,
    notes: assessment.notes ?? '',
  };
}

/** The expanded file: full contact detail, home access and every pet. */
function ClientFile({ tenantId, clientId }: { tenantId: string; clientId: string }) {
  const [client, setClient] = useState<ClientProfile | null>(null);
  const [pets, setPets] = useState<{ pet: Pet; vaccinesCurrent: boolean; documents: PetDocument[] }[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [assessmentDrafts, setAssessmentDrafts] = useState<Record<string, AssessmentDraft>>({});
  const [savingAssessmentId, setSavingAssessmentId] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const [clientResult, petResult] = await Promise.all([
          api<{ client: ClientProfile }>(`/tenants/${tenantId}/clients/${clientId}`),
          api<{ pets: Pet[] }>(`/tenants/${tenantId}/pets?clientId=${clientId}`),
        ]);

        const withDocs = await Promise.all(
          petResult.pets.map(async (pet) => {
            try {
              const docs = await api<{ documents: PetDocument[]; vaccinations: { current: boolean } }>(
                `/tenants/${tenantId}/pets/${pet.petId}/documents`
              );
              return { pet, vaccinesCurrent: docs.vaccinations.current, documents: docs.documents };
            } catch {
              return { pet, vaccinesCurrent: false, documents: [] as PetDocument[] };
            }
          })
        );

        setClient(clientResult.client);
        setPets(withDocs);
      } catch (err) {
        setError(err instanceof ApiError ? err.message : 'This file could not be opened.');
      } finally {
        setLoading(false);
      }
    })();
  }, [tenantId, clientId]);

  const review = (petId: string, documentId: string, reviewStatus: 'accepted' | 'rejected') => {
    // window.prompt returns null on cancel; normalise to undefined so the
    // optimistic update below still matches the PetDocument shape.
    const prompted =
      reviewStatus === 'rejected'
        ? window.prompt('Tell the client what is wrong with it, so they can send a better copy:')
        : null;
    if (reviewStatus === 'rejected' && !prompted) return;
    const reviewNote = prompted ?? undefined;

    void api(`/tenants/${tenantId}/pets/${petId}/documents/${documentId}`, {
      method: 'PATCH',
      body: { reviewStatus, reviewNote },
    })
      .then(() =>
        setPets((prev) =>
          prev.map((entry) =>
            entry.pet.petId !== petId
              ? entry
              : {
                  ...entry,
                  vaccinesCurrent: reviewStatus === 'accepted' ? true : entry.vaccinesCurrent,
                  documents: entry.documents.map((d) =>
                    d.documentId === documentId ? { ...d, reviewStatus, reviewNote } : d
                  ),
                }
          )
        )
      )
      .catch((err) => setError(err instanceof ApiError ? err.message : 'That could not be saved.'));
  };

  const assessmentDraftFor = (pet: Pet): AssessmentDraft => assessmentDrafts[pet.petId] ?? draftFromAssessment(pet);

  const updateAssessmentDraft = (petId: string, pet: Pet, patch: Partial<AssessmentDraft>) => {
    setAssessmentDrafts((prev) => ({
      ...prev,
      [petId]: { ...(prev[petId] ?? draftFromAssessment(pet)), ...patch },
    }));
  };

  const saveAssessment = (pet: Pet) => {
    const petId = pet.petId;
    const draft = assessmentDraftFor(pet);
    if (draft.status === 'scheduled' && !draft.scheduledFor) {
      setError('Pick a date and time for the assessment.');
      return;
    }

    setSavingAssessmentId(petId);
    void api<{ pet: Pet }>(`/tenants/${tenantId}/pets/${petId}/assessment`, {
      method: 'POST',
      body: {
        status: draft.status,
        scheduledFor: draft.status === 'scheduled' ? new Date(draft.scheduledFor).toISOString() : undefined,
        sleepoverRequired: draft.sleepoverRequired,
        notes: draft.notes || undefined,
      },
    })
      .then((result) => {
        setPets((prev) => prev.map((entry) => (entry.pet.petId === petId ? { ...entry, pet: result.pet } : entry)));
        setAssessmentDrafts((prev) => {
          const next = { ...prev };
          delete next[petId];
          return next;
        });
      })
      .catch((err) => setError(err instanceof ApiError ? err.message : 'The assessment could not be saved.'))
      .finally(() => setSavingAssessmentId(null));
  };

  const sendAgreement = () => {
    setSending(true);
    void api<{ sent: boolean; alreadySigned?: boolean }>(
      `/tenants/${tenantId}/clients/${clientId}/agreements`,
      { method: 'POST', body: {} }
    )
      .then((result) =>
        setNotice(
          result.alreadySigned
            ? 'This client has already signed for the current season.'
            : result.sent
              ? 'Signing link emailed.'
              : 'Link created, but the email did not send. Check the address on file.'
        )
      )
      .catch((err) => setError(err instanceof ApiError ? err.message : 'The link could not be sent.'))
      .finally(() => setSending(false));
  };

  if (loading) return <Spinner label="Opening the file" />;
  if (!client) return <Notice tone="error">{error ?? 'This file is not available.'}</Notice>;

  const home = client.homeAccess;

  return (
    <div className="mt-5 border-t border-[color:var(--color-rule)] pt-5">
      {notice && (
        <div className="mb-4">
          <Notice tone="ok">{notice}</Notice>
        </div>
      )}
      {error && (
        <div className="mb-4">
          <Notice tone="error">{error}</Notice>
        </div>
      )}

      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <p className="field-label">Contact</p>
          <dl>
            <Row label="Mobile">{client.mobile}</Row>
            {client.workNumber && <Row label="Work">{client.workNumber}</Row>}
            {client.address && <Row label="Address">{client.address}</Row>}
            {client.emergencyContact && (
              <Row label="Emergency">
                {client.emergencyContact.name} ({client.emergencyContact.relationship}),{' '}
                {client.emergencyContact.mobile}
              </Row>
            )}
            {client.vet && (
              <Row label="Vet">
                {client.vet.name}, {client.vet.phone}
              </Row>
            )}
          </dl>
        </div>

        {home && (
          <div>
            <p className="field-label">Home access</p>
            <dl>
              <Row label="Key">{home.providingKey ? 'Provided' : 'No key'}</Row>
              {home.entryInstructions && <Row label="Entry">{home.entryInstructions}</Row>}
              {home.entryDoor && <Row label="Door">{home.entryDoor}</Row>}
              {home.alarmPresent && <Row label="Alarm code">{home.alarmCode ?? 'Not given'}</Row>}
              {home.camerasPresent && <Row label="Cameras">{home.cameraLocations ?? 'Present'}</Row>}
              {home.restrictedAreas && <Row label="Off limits">{home.restrictedAreas}</Row>}
            </dl>
          </div>
        )}
      </div>

      <div className="mt-5">
        <button type="button" className="btn btn-secondary" onClick={sendAgreement} disabled={sending}>
          {sending ? 'Sending' : 'Send the agreement'}
        </button>
      </div>

      <div className="mt-6">
        <p className="field-label">Pets</p>
        {pets.length === 0 ? (
          <p className="text-sm text-[color:var(--color-ink-soft)]">No pets on file yet.</p>
        ) : (
          <ul className="mt-2 space-y-4">
            {pets.map(({ pet, vaccinesCurrent, documents }) => {
              const draft = assessmentDraftFor(pet);
              const saving = savingAssessmentId === pet.petId;
              return (
              <li key={pet.petId} className="border-l-2 border-[color:var(--color-rule)] pl-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="min-w-0 font-display font-bold">
                    {pet.name}
                    <span className="ml-2 typed text-sm font-normal text-[color:var(--color-ink-soft)]">
                      {[titleCase(pet.species), pet.breed].filter(Boolean).join(', ')}
                    </span>
                  </span>
                  <div className="flex flex-wrap items-center gap-2">
                    <VaccinationStamp current={vaccinesCurrent} flat />
                    <AssessmentStamp status={pet.assessment.status} flat />
                  </div>
                </div>

                <div className="mt-3 rounded-[var(--radius-card)] bg-[color:var(--color-sheet)] p-3">
                  <p className="field-label">Temperament assessment</p>
                  <div className="mt-2 grid gap-3 sm:grid-cols-2">
                    <div className="min-w-0">
                      <SelectField
                        label="Outcome"
                        value={draft.status}
                        onChange={(value) =>
                          updateAssessmentDraft(pet.petId, pet, { status: value as AssessmentDraft['status'] })
                        }
                        options={RECORDABLE_STATUSES}
                      />
                    </div>
                    {draft.status === 'scheduled' && (
                      <div className="min-w-0">
                        <TextField
                          label="When"
                          type="datetime-local"
                          value={draft.scheduledFor}
                          onChange={(value) => updateAssessmentDraft(pet.petId, pet, { scheduledFor: value })}
                          required
                        />
                      </div>
                    )}
                  </div>
                  <div className="mt-2">
                    <Toggle
                      label="Sleepover required before boarding"
                      checked={draft.sleepoverRequired}
                      onChange={(checked) => updateAssessmentDraft(pet.petId, pet, { sleepoverRequired: checked })}
                    />
                  </div>
                  <div className="mt-2 min-w-0">
                    <TextArea
                      label="Notes"
                      value={draft.notes}
                      onChange={(value) => updateAssessmentDraft(pet.petId, pet, { notes: value })}
                      rows={2}
                      placeholder="Anything the next sitter should know"
                    />
                  </div>
                  <div className="mt-3">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => saveAssessment(pet)}
                      disabled={saving}
                    >
                      {saving ? 'Saving' : 'Save assessment'}
                    </button>
                  </div>
                </div>

                {documents.length > 0 && (
                  <ul className="mt-2 space-y-1.5">
                    {documents.map((doc) => (
                      <li key={doc.documentId} className="flex flex-wrap items-center justify-between gap-2 text-sm">
                        <span>
                          {doc.label ?? doc.fileName}
                          <span className="ml-2 font-mono text-xs text-[color:var(--color-ink-faint)]">
                            {doc.expiresAt ? `due ${doc.expiresAt}` : longDate(doc.uploadedAt)}
                          </span>
                        </span>
                        <span className="flex items-center gap-2">
                          {doc.viewUrl && (
                            <a className="btn btn-quiet" href={doc.viewUrl} target="_blank" rel="noreferrer noopener">
                              View
                            </a>
                          )}
                          {doc.reviewStatus === 'pending' ? (
                            <>
                              <button
                                type="button"
                                className="btn btn-quiet"
                                onClick={() => review(pet.petId, doc.documentId, 'accepted')}
                              >
                                Accept
                              </button>
                              <button
                                type="button"
                                className="btn btn-quiet"
                                onClick={() => review(pet.petId, doc.documentId, 'rejected')}
                              >
                                Reject
                              </button>
                            </>
                          ) : (
                            <span className="font-mono text-xs text-[color:var(--color-ink-faint)]">
                              {titleCase(doc.reviewStatus)}
                            </span>
                          )}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
